import { PiazzaInputContext } from 'ComponentLibrary/src/molecules/Form/PiazzaInputContext'
import { useContext } from 'react'
import { Autocomplete as PiazzaAutocomplete, type DropdownOption } from 'ComponentLibrary/src/piazza/forms'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { Autocomplete, Box, createFilterOptions, Popper, TextField } from '@mui/material'
import { assembleNameWithEmailFallback, assembleUserSearchText, User } from 'ComponentLibrary/src/common/users'
import { InputChip } from 'ComponentLibrary/src/piazza/forms'
import { CaretDown } from 'ComponentLibrary/src/atoms/Select/Select'
import { DisplayUserLink } from 'ComponentLibrary/src/piazza/text'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

type BaseProps = {
    required?: boolean
    users: User[]
    disabled?: boolean
    label?: string
    userLinkCopy?: Partial<UserLinkCopy>
}

type MultiProps = BaseProps & {
    multiple: true
    value: User[]
    onChange: (value: User[]) => void
    customOption?: undefined
}

type SingleProps = BaseProps & {
    multiple?: false
    value: User | null
    onChange: (value: User | null) => void
    customOption?: { uuid: string; id: number; first_name: string; email: string; organization_name: string }
}

type UsersAutocompleteProps = MultiProps | SingleProps

export const UsersAutocomplete = ({
    users,
    value,
    multiple,
    disabled,
    label,
    onChange,
    customOption,
    required,
    userLinkCopy,
}: UsersAutocompleteProps) => {
    const usePiazzaInputs = useContext(PiazzaInputContext)
    if (usePiazzaInputs) {
        return (
            <PiazzaAutocomplete<User, boolean>
                multiple={multiple}
                required={required}
                label={label}
                disabled={disabled}
                options={users.map((user) => ({ value: user, label: assembleNameWithEmailFallback(user) }))}
                value={value ?? undefined}
                isValueEqual={(option, selected) => option.id === selected.id}
                onValueChange={(selected) => {
                    if (multiple) {
                        onChange((selected ?? []) as User[])
                    } else {
                        onChange((selected ?? null) as User | null)
                    }
                }}
                filterOptions={(options, state) =>
                    createFilterOptions<DropdownOption<User>>({
                        stringify: (option) => assembleUserSearchText(option.value),
                    })(
                        options.filter((option) => option.value.is_active !== false),
                        state
                    )
                }
                groupBy={(option) => option.value.organization_name}
                disableCloseOnSelect={!!multiple}
                renderOption={({ key, ...props }, { value: user }) => (
                    <li key={key} {...props}>
                        {user.id === customOption?.id ? (
                            user.first_name
                        ) : (
                            <DisplayUserLink user={user} size="medium" showProfileOnHover={false} copy={userLinkCopy} />
                        )}
                    </li>
                )}
            />
        )
    }

    return (
        <Autocomplete
            popupIcon={<CaretDown />}
            fullWidth
            multiple={multiple}
            options={users}
            value={value}
            disabled={disabled}
            onChange={(_, val) => {
                if (multiple) {
                    onChange((val ?? []) as User[])
                } else {
                    onChange(val as User | null)
                }
            }}
            getOptionLabel={(user) => assembleNameWithEmailFallback(user)}
            filterOptions={(options, state) =>
                userFilterOptions(
                    options.filter((user) => user.is_active !== false),
                    state
                )
            }
            isOptionEqualToValue={(a, b) => a.id === b.id}
            groupBy={(user) => user.organization_name}
            disableCloseOnSelect={!!multiple}
            PopperComponent={(popperProps) => <Popper {...popperProps} sx={{ ...popperProps.sx, zIndex: '1600 !important' }} />}
            renderInput={(params) => <TextField {...params} label={label} required={required} />}
            renderOption={(listAttributes, user) => (
                <li {...listAttributes} key={listAttributes.key}>
                    {user.id === customOption?.id ? (
                        user.first_name
                    ) : (
                        <DisplayUserLink user={user} size="medium" showProfileOnHover={false} copy={userLinkCopy} />
                    )}
                </li>
            )}
            renderTags={
                multiple
                    ? (selectedUsers, getTagProps) =>
                          selectedUsers.map((user, index) => (
                              <Box key={user.id} marginRight={tokens.SizingSpacing050}>
                                  <InputChip
                                      label={assembleNameWithEmailFallback(user)}
                                      deleteChip={() => getTagProps({ index }).onDelete(index)}
                                  />
                              </Box>
                          ))
                    : undefined
            }
        />
    )
}

const userFilterOptions = createFilterOptions<User>({
    stringify: assembleUserSearchText,
})
